/**
 * Generates a soft, two-tone "ding" using the Web Audio API.
 *
 * Why generate instead of bundling an .mp3?
 *  • Zero asset bytes — runs offline, no CDN dependency.
 *  • Tunable: change the notes/duration without re-encoding.
 *  • Browsers pre-warm AudioContext on the first user interaction (login/menu
 *    click), so the very first chime plays without the autoplay-policy block.
 *
 * Tone design: a quick C5 + E5 sine pair (~220 ms total) with an exponential
 * fade-out. Volume capped at 8% so it's noticeable but never startling — the
 * goal is "subtle awareness", not a CRM-style alert.
 */
let _ctx = null;

const getAudioContext = () => {
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  _ctx = new Ctor();
  return _ctx;
};

const playTone = (frequency, startOffset, duration, volume = 0.08) => {
  const ctx = getAudioContext();
  if (!ctx) return;
  const t0 = ctx.currentTime + startOffset;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.value = frequency;
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(volume, t0 + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.02);
};

export const playNotificationChime = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    // C5 = 523.25 Hz, E5 = 659.25 Hz — pleasant minor third
    playTone(523.25, 0,    0.18);
    playTone(659.25, 0.08, 0.18);
  } catch (_) {
    /* audio is a nice-to-have — never let it break the UI */
  }
};
